import { useState, useEffect, useMemo } from 'react';
import { api } from '../../lib/api';
import { Palmtree, ChevronLeft, ChevronRight, X, Check, Loader2, AlertTriangle, Calendar, Users, Star, Download, CalendarDays } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const MONTHS_FR = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const MONTHS_SHORT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MOTIFS = ['Congé payé', 'RTT', 'Maladie', 'Sans solde', 'Férié', 'Formation'];
const COLORS = ['bg-blue-500/20 text-blue-400', 'bg-emerald-500/20 text-emerald-400', 'bg-amber-500/20 text-amber-400', 'bg-purple-500/20 text-purple-400', 'bg-pink-500/20 text-pink-400', 'bg-cyan-500/20 text-cyan-400'];
const DOT_COLORS = ['bg-blue-400', 'bg-emerald-400', 'bg-amber-400', 'bg-purple-400', 'bg-pink-400', 'bg-cyan-400'];

const Conges = () => {
  const [view, setView] = useState('mois');
  const [mois, setMois] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [annee, setAnnee] = useState(() => new Date().getFullYear());
  const [salaries, setSalaries] = useState([]);
  const [calendrier, setCalendrier] = useState({});
  const [joursFeries, setJoursFeries] = useState([]);
  const [annuelData, setAnnuelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [motif, setMotif] = useState('Congé payé');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchMonthly = async () => {
    setLoading(true);
    try {
      const [congesRes, calRes] = await Promise.all([
        api.get('/paie/conges'),
        api.get(`/paie/conges/calendrier?mois=${mois}`),
      ]);
      setSalaries(congesRes.data.employes || []);
      setCalendrier(calRes.data.conges || {});
      setJoursFeries(calRes.data.jours_feries || []);
    } catch {}
    setLoading(false);
  };

  const fetchAnnuel = async () => {
    setLoading(true);
    try {
      const [congesRes, annuelRes] = await Promise.all([
        api.get('/paie/conges'),
        api.get(`/paie/conges/annuel?annee=${annee}`),
      ]);
      setSalaries(congesRes.data.employes || []);
      setAnnuelData(annuelRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    if (view === 'mois') fetchMonthly();
    else fetchAnnuel();
  }, [mois, annee, view]);

  const navigateMonth = (dir) => {
    const [y, m] = mois.split('-').map(Number);
    let nm = m + dir, ny = y;
    if (nm > 12) { nm = 1; ny++; }
    if (nm < 1) { nm = 12; ny--; }
    setMois(`${ny}-${String(nm).padStart(2, '0')}`);
  };

  const [y, m] = mois.split('-').map(Number);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(y, m - 1, 1);
    let startDay = firstDay.getDay();
    startDay = startDay === 0 ? 6 : startDay - 1;
    const daysInMonth = new Date(y, m, 0).getDate();
    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [y, m]);

  const dayEmployees = useMemo(() => {
    const map = {};
    Object.entries(calendrier).forEach(([nom, conges]) => {
      conges.forEach(c => {
        const day = parseInt(c.date.split('-')[2]);
        if (!map[day]) map[day] = [];
        map[day].push({ nom, motif: c.motif, salary_id: c.salary_id });
      });
    });
    return map;
  }, [calendrier]);

  const isWeekend = (day) => {
    if (!day) return false;
    const d = new Date(y, m - 1, day);
    return d.getDay() === 0 || d.getDay() === 6;
  };

  const feriesMap = useMemo(() => {
    const map = {};
    joursFeries.forEach(f => {
      const d = parseInt(f.date.split('-')[2]);
      map[d] = f.nom;
    });
    return map;
  }, [joursFeries]);

  const selectedFeries = useMemo(() => {
    const feriesSet = new Set(joursFeries.map(f => f.date));
    return selectedDates.filter(d => feriesSet.has(d));
  }, [selectedDates, joursFeries]);

  const feriesWarningText = useMemo(() => {
    if (selectedFeries.length === 0) return null;
    const names = selectedFeries.map(d => {
      const f = joursFeries.find(jf => jf.date === d);
      return f ? f.nom : d;
    });
    return `${names.join(', ')} ${selectedFeries.length > 1 ? 'sont des jours fériés' : 'est un jour férié'} !`;
  }, [selectedFeries, joursFeries]);

  const toggleDate = (day) => {
    if (!selectedEmployee || !day || isWeekend(day)) return;
    const dateStr = `${mois}-${String(day).padStart(2, '0')}`;
    setSelectedDates(prev =>
      prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]
    );
  };

  const isDateSelected = (day) => {
    if (!day) return false;
    return selectedDates.includes(`${mois}-${String(day).padStart(2, '0')}`);
  };

  const poserConges = async () => {
    if (!selectedEmployee || selectedDates.length === 0) return;
    setSaving(true);
    try {
      const res = await api.post('/paie/conges/poser', {
        salary_id: selectedEmployee.id,
        dates: selectedDates,
        motif,
      });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success(`${res.data.nb_jours}j posé${res.data.nb_jours > 1 ? 's' : ''} — ${res.data.conges_restants}j restants`);
        if (res.data.feries_warning) {
          toast.warning(res.data.feries_warning, { duration: 6000 });
        }
        setSelectedDates([]);
        fetchMonthly();
      }
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/paie/conges/export-pdf?annee=${annee}`);
      if (res.data.url) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        window.open(`${backendUrl}${res.data.url}`, '_blank');
        toast.success(`Export ${annee} généré`);
      }
    } catch { toast.error('Erreur export'); }
    setExporting(false);
  };

  const getEmployeeColor = (nom) => {
    const idx = salaries.findIndex(s => s.nom === nom);
    return COLORS[idx % COLORS.length];
  };

  const getEmployeeDotColor = (nom) => {
    const idx = salaries.findIndex(s => s.nom === nom);
    return DOT_COLORS[idx % DOT_COLORS.length];
  };

  const totalRestants = salaries.reduce((s, e) => s + (e.conges_restants || 0), 0);

  return (
    <div className="space-y-4" data-testid="conges-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[rgb(var(--lm-text))] flex items-center gap-2">
          <Palmtree size={20} className="text-lemas-gold" /> Congés
        </h2>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex rounded-lg overflow-hidden border border-[rgb(var(--lm-border))]" data-testid="view-toggle">
            <button onClick={() => setView('mois')}
              className={`px-3 py-1.5 text-[10px] font-medium transition-colors flex items-center gap-1 ${
                view === 'mois' ? 'bg-lemas-gold/20 text-lemas-gold' : 'text-[rgb(var(--lm-text-muted))] hover:bg-[rgb(var(--lm-surface))]'
              }`} data-testid="view-mois-btn">
              <Calendar size={10} /> Mois
            </button>
            <button onClick={() => setView('annee')}
              className={`px-3 py-1.5 text-[10px] font-medium transition-colors flex items-center gap-1 ${
                view === 'annee' ? 'bg-lemas-gold/20 text-lemas-gold' : 'text-[rgb(var(--lm-text-muted))] hover:bg-[rgb(var(--lm-surface))]'
              }`} data-testid="view-annee-btn">
              <CalendarDays size={10} /> Année
            </button>
          </div>
          {/* Navigation */}
          {view === 'mois' ? (
            <div className="flex items-center gap-2">
              <button onClick={() => navigateMonth(-1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]">
                <ChevronLeft size={16} className="text-[rgb(var(--lm-text-muted))]" />
              </button>
              <span className="text-sm font-medium text-[rgb(var(--lm-text))] min-w-[120px] text-center">
                {MONTHS_FR[m]} {y}
              </span>
              <button onClick={() => navigateMonth(1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]">
                <ChevronRight size={16} className="text-[rgb(var(--lm-text-muted))]" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => setAnnee(a => a - 1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]">
                <ChevronLeft size={16} className="text-[rgb(var(--lm-text-muted))]" />
              </button>
              <span className="text-sm font-medium text-[rgb(var(--lm-text))] min-w-[60px] text-center">{annee}</span>
              <button onClick={() => setAnnee(a => a + 1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]">
                <ChevronRight size={16} className="text-[rgb(var(--lm-text-muted))]" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-3">
        <div className="lm-card p-4 text-center">
          <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Salariés</p>
          <p className="font-mono text-xl text-[rgb(var(--lm-text))] mt-1">{salaries.length}</p>
        </div>
        <div className="lm-card p-4 text-center">
          <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Total restants</p>
          <p className={`font-mono text-xl mt-1 ${totalRestants > 50 ? 'text-[rgb(var(--lm-red))]' : 'text-lemas-gold'}`}>
            {totalRestants}j
          </p>
        </div>
        <div className="lm-card p-4 text-center">
          <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">
            {view === 'mois' ? 'Ce mois' : `Total ${annee}`}
          </p>
          <p className="font-mono text-xl text-[rgb(var(--lm-text))] mt-1">
            {view === 'mois'
              ? `${Object.values(calendrier).reduce((s, c) => s + c.length, 0)}j`
              : annuelData ? `${Object.values(annuelData.stats || {}).reduce((s, v) => s + v.total_jours, 0)}j` : '0j'
            }
          </p>
        </div>
      </div>

      {/* Tableau des soldes */}
      <div className="lm-card overflow-hidden">
        <div className="px-4 py-2 bg-[rgb(var(--lm-bg)/0.5)]">
          <p className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] flex items-center gap-1">
            <Users size={10} /> Solde des congés
          </p>
        </div>
        <div className="divide-y divide-[rgb(var(--lm-border))]">
          {salaries.map((s, i) => (
            <div key={i}
              onClick={() => {
                if (view === 'mois') {
                  setSelectedEmployee(prev => prev?.nom === s.nom ? null : { id: s.id || '', nom: s.nom });
                  setSelectedDates([]);
                }
              }}
              className={`px-4 py-3 flex items-center justify-between ${view === 'mois' ? 'cursor-pointer' : ''} transition-colors ${
                selectedEmployee?.nom === s.nom && view === 'mois'
                  ? 'bg-lemas-gold/10 border-l-2 border-lemas-gold'
                  : 'hover:bg-[rgb(var(--lm-bg)/0.3)]'
              }`}
              data-testid={`conge-row-${i}`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${COLORS[i % COLORS.length].split(' ')[0]}`} />
                <div>
                  <p className="text-sm font-medium text-[rgb(var(--lm-text))]">{s.nom}</p>
                  {s.derniere_periode && (
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Dernière MAJ: {s.derniere_periode}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Acquis</p>
                  <p className="font-mono text-xs text-[rgb(var(--lm-text-secondary))]">{s.conges_acquis}j</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Pris</p>
                  <p className="font-mono text-xs text-[rgb(var(--lm-text-secondary))]">{s.conges_pris}j</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Restants</p>
                  <p className={`font-mono text-sm font-medium ${
                    s.conges_restants > 20 ? 'text-[rgb(var(--lm-red))]' : s.conges_restants > 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-text-muted))]'
                  }`}>
                    {s.conges_restants}j
                  </p>
                </div>
                {s.conges_restants > 20 && <AlertTriangle size={14} className="text-[rgb(var(--lm-red))]" />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== MONTHLY VIEW ===== */}
      {view === 'mois' && (
        <>
          {/* Poser des congés */}
          {selectedEmployee && (
            <div className="lm-card p-4 space-y-3 animate-slide-up border border-lemas-gold/30" data-testid="poser-conge-form">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-[rgb(var(--lm-text))]">
                  Poser des congés — <span className="text-lemas-gold">{selectedEmployee.nom}</span>
                </p>
                <button onClick={() => { setSelectedEmployee(null); setSelectedDates([]); }}
                  className="p-1 rounded hover:bg-[rgb(var(--lm-surface))]">
                  <X size={14} className="text-[rgb(var(--lm-text-muted))]" />
                </button>
              </div>
              <div className="flex gap-1 flex-wrap">
                {MOTIFS.map(mt => (
                  <button key={mt} onClick={() => setMotif(mt)}
                    className={`px-2 py-1 rounded text-[10px] transition-colors ${
                      motif === mt ? 'bg-lemas-gold/20 text-lemas-gold' : 'bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))]'
                    }`}>
                    {mt}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">
                Cliquez sur les jours du calendrier ci-dessous pour sélectionner
                {selectedDates.length > 0 && (
                  <span className="text-lemas-gold ml-1">— {selectedDates.length} jour{selectedDates.length > 1 ? 's' : ''} sélectionné{selectedDates.length > 1 ? 's' : ''}</span>
                )}
              </p>
              {feriesWarningText && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="feries-warning">
                  <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                  <p className="text-[11px] text-amber-400 font-medium">{feriesWarningText}</p>
                </div>
              )}
              {selectedDates.length > 0 && (
                <Button onClick={poserConges} disabled={saving}
                  className="bg-lemas-gold text-[rgb(var(--lm-bg))] hover:bg-lemas-gold/90 text-xs"
                  data-testid="confirm-conge-btn">
                  {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}
                  Poser {selectedDates.length} jour{selectedDates.length > 1 ? 's' : ''}
                </Button>
              )}
            </div>
          )}

          {/* Calendrier mensuel */}
          <div className="lm-card p-4" data-testid="calendrier-conges">
            <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-3 flex items-center gap-1">
              <Calendar size={10} /> Calendrier
            </h3>
            <div className="grid grid-cols-7 gap-1">
              {DAYS_FR.map(d => (
                <div key={d} className="text-center text-[10px] text-[rgb(var(--lm-text-muted))] py-1 font-medium">{d}</div>
              ))}
              {calendarDays.map((day, i) => {
                if (!day) return <div key={`empty-${i}`} />;
                const weekend = isWeekend(day);
                const emps = dayEmployees[day] || [];
                const selected = isDateSelected(day);
                const today = new Date();
                const isToday = today.getFullYear() === y && today.getMonth() + 1 === m && today.getDate() === day;
                const ferieName = feriesMap[day];
                const isFerie = !!ferieName;

                return (
                  <div key={day}
                    onClick={() => toggleDate(day)}
                    className={`min-h-[48px] p-1 rounded-lg border transition-all ${
                      weekend
                        ? 'bg-[rgb(var(--lm-bg)/0.5)] border-transparent cursor-default'
                        : isFerie
                          ? selected
                            ? 'bg-amber-500/25 border-amber-500 cursor-pointer'
                            : 'bg-red-500/10 border-red-500/30 cursor-pointer'
                          : selected
                            ? 'bg-lemas-gold/20 border-lemas-gold cursor-pointer'
                            : 'border-[rgb(var(--lm-border)/0.3)] hover:border-[rgb(var(--lm-border))] cursor-pointer'
                    } ${isToday ? 'ring-1 ring-lemas-gold' : ''}`}
                    data-testid={`cal-day-${day}`}
                    title={ferieName || ''}>
                    <div className="flex items-center gap-0.5">
                      <p className={`text-[10px] font-mono ${
                        isToday ? 'text-lemas-gold font-bold' : isFerie ? 'text-red-400 font-semibold' : weekend ? 'text-[rgb(var(--lm-text-muted)/0.4)]' : 'text-[rgb(var(--lm-text-muted))]'
                      }`}>{day}</p>
                      {isFerie && <Star size={8} className="text-red-400 fill-red-400" />}
                    </div>
                    {isFerie && (
                      <p className="text-[7px] text-red-400 truncate leading-tight">{ferieName}</p>
                    )}
                    <div className="space-y-0.5 mt-0.5">
                      {emps.slice(0, 3).map((e, j) => (
                        <div key={j} className={`text-[8px] px-1 py-0.5 rounded truncate ${getEmployeeColor(e.nom)}`}>
                          {e.nom.split(' ')[0]}
                        </div>
                      ))}
                      {emps.length > 3 && (
                        <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">+{emps.length - 3}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ===== ANNUAL VIEW ===== */}
      {view === 'annee' && annuelData && (
        <>
          {/* Export button */}
          <div className="flex justify-end">
            <Button onClick={exportPDF} disabled={exporting}
              className="bg-lemas-gold text-[rgb(var(--lm-bg))] hover:bg-lemas-gold/90 text-xs gap-1"
              data-testid="export-pdf-btn">
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Exporter PDF {annee}
            </Button>
          </div>

          {/* Annual grid: employees × months */}
          <div className="lm-card overflow-x-auto" data-testid="annual-grid">
            <table className="w-full text-[10px]">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2 text-[rgb(var(--lm-text-muted))] font-medium sticky left-0 bg-[rgb(var(--lm-surface))]">Salarié</th>
                  {MONTHS_SHORT.map((ml, i) => (
                    <th key={i} className="text-center px-1 py-2 text-[rgb(var(--lm-text-muted))] font-medium min-w-[40px]">{ml}</th>
                  ))}
                  <th className="text-center px-2 py-2 text-lemas-gold font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {salaries.map((sal, si) => {
                  const empConges = annuelData.conges?.[sal.nom] || {};
                  const empStats = annuelData.stats?.[sal.nom] || { total_jours: 0 };
                  return (
                    <tr key={si} className="border-t border-[rgb(var(--lm-border)/0.2)]" data-testid={`annual-row-${si}`}>
                      <td className="px-3 py-2 sticky left-0 bg-[rgb(var(--lm-surface))]">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-full ${DOT_COLORS[si % DOT_COLORS.length]}`} />
                          <span className="text-[rgb(var(--lm-text))] font-medium whitespace-nowrap">{sal.nom}</span>
                        </div>
                      </td>
                      {Array.from({ length: 12 }, (_, mi) => {
                        const mKey = `${annee}-${String(mi + 1).padStart(2, '0')}`;
                        const days = empConges[mKey] || [];
                        const count = days.length;
                        return (
                          <td key={mi} className="text-center px-1 py-2">
                            {count > 0 ? (
                              <span className={`inline-block w-7 h-7 leading-7 rounded-md font-mono font-semibold ${COLORS[si % COLORS.length]}`}
                                title={days.map(d => d.date).join(', ')}>
                                {count}
                              </span>
                            ) : (
                              <span className="text-[rgb(var(--lm-text-muted)/0.3)]">-</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="text-center px-2 py-2">
                        <span className="font-mono font-bold text-lemas-gold text-xs">{empStats.total_jours}j</span>
                      </td>
                    </tr>
                  );
                })}
                {/* Holidays row */}
                <tr className="border-t-2 border-[rgb(var(--lm-border))]">
                  <td className="px-3 py-2 sticky left-0 bg-[rgb(var(--lm-surface))]">
                    <div className="flex items-center gap-1.5">
                      <Star size={8} className="text-red-400 fill-red-400" />
                      <span className="text-red-400 font-medium text-[10px]">Fériés</span>
                    </div>
                  </td>
                  {Array.from({ length: 12 }, (_, mi) => {
                    const mKey = `${annee}-${String(mi + 1).padStart(2, '0')}`;
                    const monthFeries = (annuelData.jours_feries || []).filter(f => f.date.startsWith(mKey));
                    return (
                      <td key={mi} className="text-center px-1 py-2">
                        {monthFeries.length > 0 ? (
                          <span className="inline-block w-7 h-7 leading-7 rounded-md bg-red-500/15 text-red-400 font-mono font-semibold"
                            title={monthFeries.map(f => `${f.date}: ${f.nom}`).join('\n')}>
                            {monthFeries.length}
                          </span>
                        ) : (
                          <span className="text-[rgb(var(--lm-text-muted)/0.3)]">-</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="text-center px-2 py-2">
                    <span className="font-mono font-bold text-red-400 text-xs">{(annuelData.jours_feries || []).length}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Holidays list for the year */}
          <div className="lm-card p-4" data-testid="feries-list">
            <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-3 flex items-center gap-1">
              <Star size={10} className="text-red-400 fill-red-400" /> Jours fériés {annee}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(annuelData.jours_feries || []).map((f, i) => {
                const dt = new Date(f.date + 'T00:00:00');
                const jourNoms = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
                const jour = jourNoms[dt.getDay()];
                const isWE = dt.getDay() === 0 || dt.getDay() === 6;
                return (
                  <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isWE ? 'bg-[rgb(var(--lm-surface))] opacity-50' : 'bg-red-500/5 border border-red-500/15'}`}>
                    <Star size={8} className="text-red-400 fill-red-400 shrink-0" />
                    <div>
                      <p className="text-[10px] font-medium text-[rgb(var(--lm-text))]">{f.nom}</p>
                      <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{jour} {f.date.split('-')[2]}/{f.date.split('-')[1]}{isWE ? ' (week-end)' : ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Légende */}
      {salaries.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          {salaries.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${COLORS[i % COLORS.length].split(' ')[0]}`} />
              <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">{s.nom}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-[rgb(var(--lm-border)/0.3)]">
            <Star size={8} className="text-red-400 fill-red-400" />
            <span className="text-[10px] text-red-400">Jour férié</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Conges;
